import { Box, CircularProgress, Divider, ListSubheader, Menu, MenuItem } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BlurOnIcon from "@mui/icons-material/BlurOn";
import CheckIcon from "@mui/icons-material/Check";
import MicIcon from "@mui/icons-material/Mic";
import VideocamIcon from "@mui/icons-material/Videocam";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import type { JSX, ReactNode } from "react";
import {
  BACKGROUND_PRESETS,
  type BackgroundMode,
} from "../media/backgroundFx.js";

/**
 * The in-call settings menu, extracted from `CallWindow.tsx`. Clearly separated
 * sections — **Cameras**, **Microphones**, **Speakers** (only where the platform
 * supports output-device selection — WKWebView/macOS-desktop hides it), and
 * **Camera effects** (None / Blur / virtual backgrounds) — each under a labelled,
 * icon'd header. Picking a mic/camera switches the live track; picking a speaker
 * calls `onPickAudioOutput` (the caller applies `setSinkId`); picking a
 * background calls `onPickBackground`. The selected row (the remembered device,
 * or the OS default when nothing is pinned) shows a check. While an effect is
 * initialising (`backgroundBusy`, the first-time model load) the selected effect
 * shows a spinner + "Loading…" so it's clear something is happening. Anchored
 * ABOVE the floating window (which sits at zIndex.modal+1, so the menu clears it).
 */

/** A labelled section header with an icon (non-interactive). */
function SectionHeader({ icon, label }: { icon: ReactNode; label: string }): JSX.Element {
  return (
    <ListSubheader
      disableSticky
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        lineHeight: "34px",
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        color: "text.secondary",
        bgcolor: "transparent",
      }}
    >
      <Box sx={{ display: "flex", "& svg": { fontSize: 16 } }}>{icon}</Box>
      {label}
    </ListSubheader>
  );
}

/** Fixed-width leading slot so labels align whether or not the row is selected. */
function LeadSlot({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Box sx={{ width: 20, mr: 1, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {children}
    </Box>
  );
}

export function DeviceMenu({
  anchorEl,
  onClose,
  audioInputs,
  videoInputs,
  audioOutputs,
  selectedAudioId,
  selectedVideoId,
  selectedAudioOutputId,
  onPickAudio,
  onPickVideo,
  onPickAudioOutput,
  showSpeakers,
  showBackground,
  backgroundMode,
  backgroundBusy,
  onPickBackground,
}: {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  audioInputs: MediaDeviceInfo[];
  videoInputs: MediaDeviceInfo[];
  audioOutputs: MediaDeviceInfo[];
  selectedAudioId: string | null;
  selectedVideoId: string | null;
  selectedAudioOutputId: string | null;
  onPickAudio: (deviceId: string) => void;
  onPickVideo: (deviceId: string) => void;
  onPickAudioOutput: (deviceId: string) => void;
  showSpeakers: boolean;
  showBackground: boolean;
  backgroundMode: BackgroundMode;
  backgroundBusy: boolean;
  onPickBackground: (mode: BackgroundMode) => void;
}): JSX.Element {
  const renderBackgroundItem = (
    mode: BackgroundMode,
    label: string,
    swatch: JSX.Element,
  ): JSX.Element => {
    const selected = backgroundMode === mode;
    const loading = selected && backgroundBusy;
    return (
      <MenuItem
        key={`bg-${mode}`}
        data-testid={`call-bg-${mode}`}
        selected={selected}
        onClick={() => onPickBackground(mode)}
      >
        <LeadSlot>
          {loading ? (
            <CircularProgress size={14} thickness={5} data-testid={`call-bg-${mode}-loading`} />
          ) : selected ? (
            <CheckIcon fontSize="small" />
          ) : null}
        </LeadSlot>
        {swatch}
        <Box component="span" sx={{ ml: 1 }}>{label}</Box>
        {loading && (
          <Box component="span" sx={{ ml: "auto", pl: 1.5, fontSize: 12, color: "text.secondary" }}>
            Loading…
          </Box>
        )}
      </MenuItem>
    );
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={anchorEl !== null}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      // The floating call window sits at zIndex.modal+1, so the menu
      // must go ABOVE it (it defaults to zIndex.modal → renders behind).
      sx={{ zIndex: (t) => t.zIndex.modal + 2, "& .MuiPaper-root": { minWidth: 248 } }}
    >
      <SectionHeader icon={<VideocamIcon />} label="Cameras" />
      {videoInputs.length === 0 && <MenuItem disabled>No cameras found</MenuItem>}
      {videoInputs.map((d, i) => {
        const selected = selectedVideoId === d.deviceId;
        return (
          <MenuItem key={d.deviceId || `cam-${i}`} selected={selected} onClick={() => onPickVideo(d.deviceId)}>
            <LeadSlot>{selected && <CheckIcon fontSize="small" />}</LeadSlot>
            {d.label || `Camera ${i + 1}`}
          </MenuItem>
        );
      })}

      <Divider sx={{ my: 0.5 }} />
      <SectionHeader icon={<MicIcon />} label="Microphones" />
      {audioInputs.length === 0 && <MenuItem disabled>No microphones found</MenuItem>}
      {audioInputs.map((d, i) => {
        const selected = selectedAudioId === d.deviceId;
        return (
          <MenuItem key={d.deviceId || `mic-${i}`} selected={selected} onClick={() => onPickAudio(d.deviceId)}>
            <LeadSlot>{selected && <CheckIcon fontSize="small" />}</LeadSlot>
            {d.label || `Microphone ${i + 1}`}
          </MenuItem>
        );
      })}

      {showSpeakers && <Divider sx={{ my: 0.5 }} />}
      {showSpeakers && <SectionHeader icon={<VolumeUpIcon />} label="Speakers" />}
      {showSpeakers && audioOutputs.length === 0 && <MenuItem disabled>No speakers found</MenuItem>}
      {showSpeakers &&
        audioOutputs.map((d, i) => {
          const selected = selectedAudioOutputId === d.deviceId;
          return (
            <MenuItem
              key={d.deviceId || `spk-${i}`}
              selected={selected}
              onClick={() => onPickAudioOutput(d.deviceId)}
            >
              <LeadSlot>{selected && <CheckIcon fontSize="small" />}</LeadSlot>
              {d.label || `Speaker ${i + 1}`}
            </MenuItem>
          );
        })}

      {showBackground && <Divider sx={{ my: 0.5 }} />}
      {showBackground && <SectionHeader icon={<AutoAwesomeIcon />} label="Camera effects" />}
      {showBackground &&
        renderBackgroundItem(
          "none",
          "None",
          <VideocamIcon fontSize="small" sx={{ color: "text.secondary" }} />,
        )}
      {showBackground &&
        renderBackgroundItem(
          "blur",
          "Blur",
          <BlurOnIcon fontSize="small" sx={{ color: "text.secondary" }} />,
        )}
      {showBackground &&
        BACKGROUND_PRESETS.map((p) =>
          renderBackgroundItem(
            p.id,
            p.label,
            <Box
              aria-hidden
              sx={{
                width: 22,
                height: 16,
                borderRadius: 0.5,
                border: "1px solid",
                borderColor: "divider",
                backgroundImage: `url(${p.url})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />,
          ),
        )}
    </Menu>
  );
}
